import React, { useState } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import {
  getDaysInMonth, getFirstDayOfMonth, toKey,
  itemHitsOnDate, getRecurringAmount, getRecurringItemsForDay, getDayBalance,
} from '../utils/calendarMath';

const FREQS = ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Yearly'];
const getFirstOfMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};
const newEmptyDraft = () => ({ name: '', amount: '', type: 'expense', frequency: 'Monthly', lastOccurrence: getFirstOfMonth() });
const EMPTY_ITEM_DRAFT = newEmptyDraft();

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const newId = () => Math.random().toString(36).substr(2, 9);
const fmt = (n) => '$' + Number(n).toFixed(2);


export default function CalendarView({
  budgetItems,
  endOfDayBalance,
  oneOffTransactions,
  skippedOccurrences = {},
  onUpdateBalance,
  onUpdateTransactions,
  onBudgetItemsChange,
  onUpdateSkips,
  isPro,
  currency,
  onAddToSpendingLog = null,
}) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t, isDark } = useTheme();
  const todayObj = new Date();
  const [displayYear, setDisplayYear] = useState(todayObj.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(todayObj.getMonth());
  const [balanceInput, setBalanceInput] = useState(String(endOfDayBalance));
  const [selectedDay, setSelectedDay] = useState(null);
  const [txnDraft, setTxnDraft] = useState({ name: '', amount: '' });
  const [itemDraft, setItemDraft] = useState(newEmptyDraft);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [showNavPrompt, setShowNavPrompt] = useState(false);

  const today = {
    day: todayObj.getDate(),
    month: todayObj.getMonth(),
    year: todayObj.getFullYear(),
  };

  const prevMonth = () => {
    if (!isPro) {
      setShowNavPrompt(true);
      return;
    }
    if (displayMonth === 0) {
      setDisplayYear((y) => y - 1);
      setDisplayMonth(11);
    } else {
      setDisplayMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    // Limit forward navigation: free = current + 1, Pro = current + 12
    const maxFwdMonths = isPro ? 12 : 1;
    const todayYear = todayObj.getFullYear();
    const todayMonth = todayObj.getMonth();
    const diffMonths = (displayYear - todayYear) * 12 + (displayMonth - todayMonth);
    if (diffMonths >= maxFwdMonths) {
      if (!isPro) setShowNavPrompt(true);
      return;
    }
    if (displayMonth === 11) {
      setDisplayYear((y) => y + 1);
      setDisplayMonth(0);
    } else {
      setDisplayMonth((m) => m + 1);
    }
  };

  const isToday = (day) =>
    day === today.day && displayMonth === today.month && displayYear === today.year;

  const isPast = (day) => {
    if (displayYear < today.year) return true;
    if (displayYear > today.year) return false;
    if (displayMonth < today.month) return true;
    if (displayMonth > today.month) return false;
    return day < today.day;
  };

  const dateKey = (day) => toKey(displayYear, displayMonth, day);

  const hasOneOff = (day) => {
    const txns = oneOffTransactions[dateKey(day)];
    return txns && txns.length > 0;
  };

  const hasRecurring = (day) =>
    budgetItems.some((item) => item.frequency !== 'Daily' && itemHitsOnDate(item, displayYear, displayMonth, day));

  const addOneOff = (day) => {
    const key = dateKey(day);
    const txn = {
      id: newId(),
      name: txnDraft.name || 'Transaction',
      amount: parseFloat(txnDraft.amount) || 0,
    };
    const existing = oneOffTransactions[key] || [];
    onUpdateTransactions({ ...oneOffTransactions, [key]: [...existing, txn] });
    if (isPro && onAddToSpendingLog) {
      onAddToSpendingLog({
        id: newId(),
        date: dateKey(day),
        name: txn.name,
        category: 'Other Expenses',
        amount: Math.abs(txn.amount),
        type: txn.amount >= 0 ? 'expense' : 'income',
      });
    }
    setTxnDraft({ name: '', amount: '' });
    setSelectedDay(null);
  };

  const removeOneOff = (day, txnId) => {
    const key = dateKey(day);
    const filtered = (oneOffTransactions[key] || []).filter((t) => t.id !== txnId);
    const updated = { ...oneOffTransactions };
    if (filtered.length === 0) delete updated[key];
    else updated[key] = filtered;
    onUpdateTransactions(updated);
  };

  // Budget item helpers
  const editableItems = budgetItems.filter((i) => !i.isDebtPayment);
  const debtItems     = budgetItems.filter((i) =>  i.isDebtPayment);

  const addBudgetItem = () => {
    if (!itemDraft.name.trim() || !itemDraft.amount) return;
    const newItem = {
      id: newId(),
      name: itemDraft.name.trim(),
      amount: parseFloat(itemDraft.amount) || 0,
      type: itemDraft.type,
      frequency: itemDraft.frequency,
      lastOccurrence: itemDraft.lastOccurrence || null,
    };
    onBudgetItemsChange([...editableItems, newItem]);
    setItemDraft(newEmptyDraft());
    setShowAddItem(false);
  };

  const deleteBudgetItem = (id) =>
    onBudgetItemsChange(editableItems.filter((i) => i.id !== id));

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({ name: item.name, amount: String(item.amount), type: item.type || 'expense', frequency: item.frequency, lastOccurrence: item.lastOccurrence || '' });
  };

  const saveEdit = () => {
    if (!editDraft.name.trim()) return;
    onBudgetItemsChange(editableItems.map((item) =>
      item.id === editingId
        ? { ...item, name: editDraft.name.trim(), amount: parseFloat(editDraft.amount) || 0, type: editDraft.type, frequency: editDraft.frequency, lastOccurrence: editDraft.lastOccurrence || null }
        : item
    ));
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const skipOccurrence = (itemId, day) => {
    const key = dateKey(day);
    const existing = skippedOccurrences[key] || [];
    if (!existing.includes(itemId)) {
      onUpdateSkips({ ...skippedOccurrences, [key]: [...existing, itemId] });
    }
  };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(displayYear, displayMonth);
  const firstDay = getFirstDayOfMonth(displayYear, displayMonth);

  // Month summary for banner
  const monthSummary = (() => {
    let firstNegDay = null;
    let endBalance = null;
    for (let d = 1; d <= daysInMonth; d++) {
      const bal = getDayBalance(displayYear, displayMonth, d, endOfDayBalance, budgetItems, oneOffTransactions, skippedOccurrences);
      if (firstNegDay === null && bal < 0) firstNegDay = d;
      if (d === daysInMonth) endBalance = bal;
    }
    return { firstNegDay, endBalance };
  })();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  // Responsive values
  const pagePadding = isDesktop ? '20px' : isTablet ? '16px' : '8px';
  const layoutDirection = isMobile ? 'column' : 'row';
  const sidebarWidth = isMobile ? 'auto' : isTablet ? '220px' : '300px';
  const sidebarFlexShrink = isMobile ? 1 : 0;
  const cellMinHeight = isDesktop ? '115px' : isTablet ? '80px' : '52px';
  const cellPadding = isMobile ? '3px' : '8px';
  const showSingleLetter = isMobile || isTablet;
  const balanceInputWidth = isDesktop ? '110px' : isTablet ? '100px' : '90px';

  // Abbreviated amount for mobile cells (e.g. $1.2k)
  const fmtShort = (n) => {
    const val = Number(n);
    const abs = Math.abs(val);
    const sign = val < 0 ? '-' : '';
    if (abs >= 10000) return sign + sym + Math.round(abs / 1000) + 'k';
    if (abs >= 1000) return sign + sym + (abs / 1000).toFixed(1) + 'k';
    return sign + sym + abs.toFixed(0);
  };

  return (
    <div style={{ ...styles.page, background: t.pageBg, padding: pagePadding }}>
      {/* Balance bar */}
      <div style={styles.topBar}>
        <span style={{ ...styles.balanceLabel, display: isMobile ? 'none' : 'inline', color: t.textSub }}>End of Day Balance (Today):</span>
        <input
          type="number"
          value={balanceInput}
          onChange={(e) => setBalanceInput(e.target.value)}
          style={{ ...styles.balanceInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, width: balanceInputWidth }}
          placeholder={isMobile ? 'Balance' : undefined}
        />
        <button
          style={styles.greenBtn}
          onClick={() => onUpdateBalance(parseFloat(balanceInput) || 0)}
        >
          Update
        </button>
      </div>

      {/* Main layout */}
      <div style={{ ...styles.layout, flexDirection: layoutDirection }}>
        {/* Calendar section */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Month navigation */}
          <div style={{ ...styles.monthNav, marginBottom: isMobile ? '8px' : '16px' }}>
            <button style={{ ...styles.navBtn, width: isMobile ? '30px' : '36px', height: isMobile ? '30px' : '36px', fontSize: isMobile ? '16px' : '20px' }} onClick={prevMonth}>
              ‹
            </button>
            <h2 style={{ ...styles.monthTitle, color: t.text, fontSize: isMobile ? '15px' : '20px', minWidth: isMobile ? '130px' : '180px' }}>
              {MONTHS[displayMonth]} {displayYear}
            </h2>
            <button style={{ ...styles.navBtn, width: isMobile ? '30px' : '36px', height: isMobile ? '30px' : '36px', fontSize: isMobile ? '16px' : '20px' }} onClick={nextMonth}>
              ›
            </button>
          </div>

          {/* Free-tier nav prompt */}
          {showNavPrompt && (
            <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: t.textMuted }}>Upgrade to Pro to navigate to other months.</span>
              <button onClick={() => setShowNavPrompt(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textFaintest, fontSize: '16px', padding: '0 4px', fontFamily: 'inherit', lineHeight: 1 }}>✕</button>
            </div>
          )}

          {/* Month summary banner */}
          {(() => {
            const { firstNegDay, endBalance } = monthSummary;
            const monthLabel = `${MONTHS[displayMonth]} ${displayYear}`;
            const fmtBal = (n) => '$' + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            const negColor = isDark ? '#ff8a80' : '#d93025';
            const posColor = isDark ? '#81c784' : '#1b6d24';
            let content;
            if (firstNegDay !== null && endBalance !== null && endBalance >= 0) {
              const negDate = new Date(displayYear, displayMonth, firstNegDay)
                .toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
              content = (
                <span>
                  Your balance may dip below $0 on{' '}
                  <strong style={{ color: negColor }}>{negDate}</strong>
                  {', '}but recovers to{' '}
                  <strong style={{ color: posColor }}>{fmtBal(endBalance)}</strong>
                  {' '}by month end
                </span>
              );
            } else if (firstNegDay !== null) {
              const negDate = new Date(displayYear, displayMonth, firstNegDay)
                .toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
              content = (
                <span style={{ color: negColor }}>
                  Your balance is projected to drop below $0 on{' '}
                  <strong>{negDate}</strong>
                </span>
              );
            } else {
              content = (
                <span>
                  By the end of {monthLabel}, your balance will be approximately{' '}
                  <strong style={{ color: posColor }}>{fmtBal(endBalance)}</strong>
                </span>
              );
            }
            return (
              <div style={{
                background: t.card,
                border: `1px solid ${t.borderFaint}`,
                borderRadius: '8px',
                padding: isMobile ? '8px 12px' : isTablet ? '10px 14px' : '11px 18px',
                marginBottom: '10px',
                fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                color: t.textSecondary,
                lineHeight: '1.5',
              }}>
                {content}
              </div>
            );
          })()}

          {/* Calendar grid */}
          <div style={{ ...styles.calCard, background: t.card }}>
            {/* Headers */}
            <div style={styles.headerRow}>
              {DAY_HEADERS.map((d) => (
                <div key={d} style={{ ...styles.dayHeader, background: t.tableSubHeader, color: t.textSub, padding: isMobile ? '4px 2px' : '10px', fontSize: isMobile ? '10px' : '14px' }}>
                  {showSingleLetter ? d[0] : d}
                </div>
              ))}
            </div>

            {/* Weeks */}
            {weeks.map((week, wi) => (
              <div key={wi} style={styles.weekRow}>
                {week.map((day, di) => {
                  const past = day ? isPast(day) : false;
                  const isSelected = day && selectedDay === day;
                  const chipColor = day ? (isDark ? '#0f3358' : (isToday(day) ? '#0061a4' : 'rgba(27,109,36,0.1)')) : 'transparent';
                  const chipTextColor = isDark ? '#ffffff' : (isToday(day) ? 'white' : '#1b6d24');
                  const pastBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)';
                  const dayBalance = day ? getDayBalance(displayYear, displayMonth, day, endOfDayBalance, budgetItems, oneOffTransactions, skippedOccurrences) : 0;
                  const oneOffs = day ? (oneOffTransactions[dateKey(day)] || []) : [];
                  const recurring = day ? getRecurringItemsForDay(budgetItems, displayYear, displayMonth, day, skippedOccurrences[dateKey(day)] || []) : [];

                  return (
                    <div
                      key={di}
                      onClick={() => isMobile && day && setSelectedDay(selectedDay === day ? null : day)}
                      style={{
                        ...styles.dayCell,
                        background: past ? pastBg : t.card,
                        borderRight: `1px solid ${t.borderLight}`,
                        borderBottom: `1px solid ${t.borderLight}`,
                        minHeight: cellMinHeight,
                        padding: cellPadding,
                        cursor: isMobile && day ? 'pointer' : 'default',
                        ...(day && isToday(day)
                          ? { outline: '2px solid #0061a4', outlineOffset: '-2px', border: 'none', background: isDark ? '#07253f' : 'rgba(0,97,164,0.05)' }
                          : {}),
                        ...(isMobile && isSelected && !isToday(day)
                          ? { outline: `2px solid ${t.border}`, outlineOffset: '-2px' }
                          : {}),
                        ...(!day ? { ...styles.emptyCell, background: t.tableRowAlt, borderRight: `1px solid ${t.borderLight}`, borderBottom: `1px solid ${t.borderLight}`, minHeight: cellMinHeight } : {}),
                      }}
                    >
                      {day && (
                        <>
                          {/* Mobile: compact day number + tiny balance amount inline */}
                          {isMobile ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
                              <span style={{
                                fontSize: '10px', fontWeight: isToday(day) ? '700' : '500',
                                color: past ? t.textFaintest : t.textSub,
                                lineHeight: 1,
                              }}>{day}</span>
                              <span style={{
                                fontSize: '9px', color: past ? t.textFaintest : (dayBalance < 0 ? (isDark ? '#ef9a9a' : '#ba1a1a') : (isDark ? '#81c784' : '#1b6d24')),
                                opacity: past ? 0.5 : 1, lineHeight: 1,
                              }}>{fmtShort(dayBalance)}</span>
                            </div>
                          ) : (
                            <div style={{ ...styles.dayCellTop, marginBottom: '6px' }}>
                              <span style={{ ...styles.dayNum, color: past ? t.textFaintest : t.textSub, fontSize: '14px' }}>{day}</span>
                              <button
                                style={styles.plusBtn}
                                onClick={(e) => { e.stopPropagation(); setSelectedDay(selectedDay === day ? null : day); }}
                              >+</button>
                            </div>
                          )}

                          {/* Balance chip — desktop/tablet only */}
                          {!isMobile && (
                            <div style={{ ...styles.balanceChip, background: chipColor, color: chipTextColor, fontSize: '12px', padding: '3px 8px', opacity: past ? 0.5 : 1 }}>
                              {fmt(dayBalance)}
                            </div>
                          )}

                          {/* One-off transactions */}
                          {oneOffs.slice(0, isMobile ? 2 : undefined).map((txn) => (
                            <div key={txn.id} style={{ ...styles.txnTag, background: isDark ? '#7a6200' : '#fef3c7', color: isDark ? '#f0f6fc' : '#555', opacity: past ? 0.6 : 1, fontSize: isMobile ? '9px' : '11px', padding: isMobile ? '1px 3px' : '2px 4px', marginTop: isMobile ? '2px' : '3px' }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                {isMobile ? txn.name : `${txn.name}: ${fmt(txn.amount)}`}
                              </span>
                              {!isMobile && (
                                <button style={{ ...styles.txnRemoveBtn, color: isDark ? '#8b949e' : '#aaa' }} onClick={() => removeOneOff(day, txn.id)}>×</button>
                              )}
                            </div>
                          ))}
                          {isMobile && oneOffs.length > 2 && (
                            <span style={{ fontSize: '9px', color: t.textFaintest, marginTop: '1px', display: 'block' }}>+{oneOffs.length - 2} more</span>
                          )}

                          {/* Recurring budget items */}
                          {recurring.slice(0, isMobile ? 2 : undefined).map((item) => {
                            const isIncome = item.type === 'income';
                            const tagBg = isIncome
                              ? (isDark ? '#1a3d20' : 'rgba(27,109,36,0.1)')
                              : (isDark ? '#3d1a1a' : 'rgba(186,26,26,0.08)');
                            return (
                              <div key={item.id} style={{ ...styles.txnTag, background: tagBg, color: isDark ? '#f0f6fc' : (isIncome ? '#1b6d24' : '#ba1a1a'), opacity: past ? 0.6 : 1, fontSize: isMobile ? '9px' : '11px', padding: isMobile ? '1px 3px' : '2px 4px', marginTop: isMobile ? '2px' : '3px' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                  {isMobile ? item.name : `${item.name}: ${fmt(item.amount)}`}
                                </span>
                                {!isMobile && (
                                  <button onClick={() => skipOccurrence(item.id, day)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isDark ? '#8b949e' : '#aaa', fontSize: '12px', padding: '0 2px', lineHeight: 1, fontFamily: 'inherit', flexShrink: 0 }}>×</button>
                                )}
                              </div>
                            );
                          })}
                          {isMobile && recurring.length > 2 && (
                            <span style={{ fontSize: '9px', color: t.textFaintest, marginTop: '1px', display: 'block' }}>+{recurring.length - 2} more</span>
                          )}

                          {/* Add one-off form — desktop/tablet only, inline in cell */}
                          {!isMobile && selectedDay === day && (
                            <div style={styles.txnForm}>
                              <input
                                type="text"
                                placeholder="Name"
                                value={txnDraft.name}
                                onChange={(e) => setTxnDraft((p) => ({ ...p, name: e.target.value }))}
                                style={{ ...styles.txnInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                                autoFocus
                              />
                              <input
                                type="number"
                                placeholder="Amount"
                                value={txnDraft.amount}
                                onChange={(e) => setTxnDraft((p) => ({ ...p, amount: e.target.value }))}
                                style={{ ...styles.txnInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                                step="0.01"
                                onKeyDown={(e) => e.key === 'Enter' && addOneOff(day)}
                              />
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button onClick={() => addOneOff(day)} style={styles.txnAddBtn}>Add</button>
                                <button onClick={() => setSelectedDay(null)} style={styles.txnCancelBtn}>✕</button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Mobile: add transaction panel shown below the grid when a day is selected */}
          {isMobile && selectedDay && (
            <div style={{ marginTop: '10px', background: t.card, borderRadius: '10px', padding: '12px', border: `1px solid ${t.borderLight}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: t.text }}>
                  Add to {MONTHS[displayMonth]} {selectedDay}
                </span>
                <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textFaintest, fontSize: '16px', padding: '0 4px', fontFamily: 'inherit', lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <input
                  type="text"
                  placeholder="Name"
                  value={txnDraft.name}
                  onChange={(e) => setTxnDraft((p) => ({ ...p, name: e.target.value }))}
                  style={{ ...styles.txnInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, padding: '8px 10px', fontSize: '14px' }}
                  autoFocus
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={txnDraft.amount}
                  onChange={(e) => setTxnDraft((p) => ({ ...p, amount: e.target.value }))}
                  style={{ ...styles.txnInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, padding: '8px 10px', fontSize: '14px' }}
                  step="0.01"
                  onKeyDown={(e) => e.key === 'Enter' && addOneOff(selectedDay)}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                  <button onClick={() => addOneOff(selectedDay)} style={{ ...styles.txnAddBtn, flex: 1, padding: '10px', fontSize: '14px' }}>Add</button>
                  <button onClick={() => setSelectedDay(null)} style={{ ...styles.txnCancelBtn, padding: '10px 16px', fontSize: '14px' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div style={{ ...styles.legend, marginTop: isMobile ? '10px' : '14px', gap: isMobile ? '12px' : '24px' }}>
            <LegendItem color={isDark ? '#0f3358' : '#0061a4'} label="Today Balance" />
            <LegendItem color={isDark ? '#1a3d20' : 'rgba(27,109,36,0.1)'} label="Income" />
            <LegendItem color={isDark ? '#3d1a1a' : 'rgba(186,26,26,0.08)'} label="Expense" />
            <LegendItem color={isDark ? '#7a6200' : '#fef3c7'} label="One-Off" />
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ ...styles.sidebar, background: t.card, width: sidebarWidth, flexShrink: sidebarFlexShrink, marginTop: isMobile ? '12px' : '0' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ ...styles.sidebarTitle, color: t.text, margin: 0 }}>Budget Items</h3>
            <button
              onClick={() => setShowAddItem((v) => !v)}
              style={{
                background: showAddItem ? t.borderLight : '#1b6d24',
                color: showAddItem ? t.textSecondary : 'white',
                border: 'none', borderRadius: '6px',
                padding: '4px 10px', fontSize: '12px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {showAddItem ? 'Cancel' : '+ Add'}
            </button>
          </div>

          {/* Add item form */}
          {showAddItem && (
            <div style={{
              background: t.pageBg, borderRadius: '8px', padding: '12px',
              marginBottom: '12px', border: `1px solid ${t.borderLight}`,
              display: 'flex', flexDirection: 'column', gap: '8px',
            }}>
              <input
                type="text"
                placeholder="Item name…"
                value={itemDraft.name}
                onChange={(e) => setItemDraft((p) => ({ ...p, name: e.target.value }))}
                autoFocus
                style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
              />
              {/* Type toggle */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {['income', 'expense'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setItemDraft((p) => ({ ...p, type }))}
                    style={{
                      flex: 1, padding: '5px', border: 'none', borderRadius: '5px',
                      fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                      background: itemDraft.type === type
                        ? (type === 'income' ? '#1b6d24' : '#ba1a1a')
                        : t.borderLight,
                      color: itemDraft.type === type ? 'white' : t.textMuted,
                    }}
                  >
                    {type === 'income' ? '↑ Income' : '↓ Expense'}
                  </button>
                ))}
              </div>
              <input
                type="number"
                placeholder="Amount"
                value={itemDraft.amount}
                onChange={(e) => setItemDraft((p) => ({ ...p, amount: e.target.value }))}
                min="0" step="0.01"
                style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
              />
              <select
                value={itemDraft.frequency}
                onChange={(e) => setItemDraft((p) => ({ ...p, frequency: e.target.value }))}
                style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, cursor: 'pointer' }}
              >
                {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <div>
                <label style={{ fontSize: '11px', color: t.textMuted, display: 'block', marginBottom: '3px' }}>
                  Last occurrence (optional)
                </label>
                <input
                  type="date"
                  value={itemDraft.lastOccurrence}
                  onChange={(e) => setItemDraft((p) => ({ ...p, lastOccurrence: e.target.value }))}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                />
              </div>
              <button
                onClick={addBudgetItem}
                style={{
                  background: '#1b6d24', color: 'white', border: 'none',
                  borderRadius: '6px', padding: '8px', fontSize: '13px',
                  fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Add Item
              </button>
            </div>
          )}

          {/* Editable items */}
          {editableItems.length === 0 && debtItems.length === 0 && !showAddItem && (
            <p style={{ ...styles.emptyMsg, color: t.textMuted }}>
              No budget items yet. Click "+ Add" to create one.
            </p>
          )}

          {editableItems.map((item) => (
            <div key={item.id} style={{ ...styles.sidebarItem, borderBottom: `1px solid ${t.borderLight}` }}>
              {editingId === item.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <input
                    type="text"
                    value={editDraft.name}
                    onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
                    style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {['income', 'expense'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setEditDraft((p) => ({ ...p, type }))}
                        style={{
                          flex: 1, padding: '4px', border: 'none', borderRadius: '4px',
                          fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                          background: editDraft.type === type ? (type === 'income' ? '#1b6d24' : '#ba1a1a') : t.borderLight,
                          color: editDraft.type === type ? 'white' : t.textMuted,
                        }}
                      >
                        {type === 'income' ? '↑ Income' : '↓ Expense'}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    value={editDraft.amount}
                    onChange={(e) => setEditDraft((p) => ({ ...p, amount: e.target.value }))}
                    min="0" step="0.01"
                    style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                  />
                  <select
                    value={editDraft.frequency}
                    onChange={(e) => setEditDraft((p) => ({ ...p, frequency: e.target.value }))}
                    style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, cursor: 'pointer' }}
                  >
                    {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <div>
                    <label style={{ fontSize: '11px', color: t.textMuted, display: 'block', marginBottom: '2px' }}>Last occurrence</label>
                    <input
                      type="date"
                      value={editDraft.lastOccurrence}
                      onChange={(e) => setEditDraft((p) => ({ ...p, lastOccurrence: e.target.value }))}
                      max={new Date().toISOString().split('T')[0]}
                      style={{ ...styles.sideInput, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={saveEdit} style={{ flex: 1, background: '#1b6d24', color: 'white', border: 'none', borderRadius: '5px', padding: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
                    <button onClick={cancelEdit} style={{ background: t.borderLight, color: t.textSecondary, border: 'none', borderRadius: '5px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: item.type === 'income' ? '#1b6d24' : '#ba1a1a', flexShrink: 0 }} />
                      <span style={{ ...styles.sidebarItemName, color: t.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                      </span>
                    </div>
                    <div style={{ ...styles.sidebarItemDetail, color: t.textMuted }}>
                      ${Number(item.amount).toFixed(2)} / {item.frequency}
                    </div>
                    {item.lastOccurrence && (
                      <div style={{ fontSize: '11px', color: t.textFaint, marginTop: '2px' }}>
                        Last: {new Date(item.lastOccurrence + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                    <button onClick={() => startEdit(item)} style={{ background: 'none', border: 'none', color: t.textFaint, cursor: 'pointer', fontSize: '13px', padding: '0 4px', lineHeight: 1, fontFamily: 'inherit' }}>✎</button>
                    <button onClick={() => deleteBudgetItem(item.id)} style={{ background: 'none', border: 'none', color: t.textFaintest, cursor: 'pointer', fontSize: '16px', padding: '0 2px', lineHeight: 1, fontFamily: 'inherit' }}>×</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Debt payment items — read-only */}
          {debtItems.length > 0 && (
            <>
              <div style={{ fontSize: '10px', fontWeight: '700', color: t.textFaint, letterSpacing: '0.05em', padding: '10px 0 4px', textTransform: 'uppercase' }}>
                Debt Payments
              </div>
              {debtItems.map((item) => (
                <div key={item.id} style={{ ...styles.sidebarItem, borderBottom: `1px solid ${t.borderLight}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', background: 'rgba(154,37,174,0.1)', color: '#9a25ae', flexShrink: 0 }}>DEBT</span>
                    <span style={{ ...styles.sidebarItemName, color: t.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  </div>
                  <div style={{ ...styles.sidebarItemDetail, color: t.textMuted }}>
                    ${Number(item.amount).toFixed(2)} / {item.frequency}
                  </div>
                </div>
              ))}
            </>
          )}

        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, label, border }) {
  const { t } = useTheme();
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div
        style={{
          width: isMobile ? '20px' : '32px',
          height: isMobile ? '10px' : '16px',
          borderRadius: '4px',
          background: color,
          border: border ? `1px solid ${t.borderFaint}` : 'none',
          flexShrink: 0,
        }}
      />
      <span style={{ color: t.textTertiary, fontSize: isMobile ? '10px' : '13px' }}>{label}</span>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    padding: '20px',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    maxWidth: '1400px',
    margin: '0 auto 12px',
  },
  balanceLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  balanceInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    width: '110px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  blueBtn: {
    background: '#0061a4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'inherit',
  },
  greenBtn: {
    background: '#1b6d24',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'inherit',
  },
  layout: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  monthNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '16px',
  },
  navBtn: {
    background: '#0061a4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    cursor: 'pointer',
    fontSize: '20px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    lineHeight: 1,
  },
  monthTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    minWidth: '180px',
    textAlign: 'center',
  },
  calCard: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
  },
  dayHeader: {
    padding: '10px',
    textAlign: 'center',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  weekRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
  },
  dayCell: {
    minHeight: '115px',
    padding: '8px',
    borderRight: '1px solid #e8e8e8',
    borderBottom: '1px solid #e8e8e8',
    background: 'white',
    position: 'relative',
  },
  emptyCell: {
    minHeight: '115px',
    background: '#fafafa',
    borderRight: '1px solid #e8e8e8',
    borderBottom: '1px solid #e8e8e8',
  },
  dayCellTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  dayNum: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  plusBtn: {
    background: '#1b6d24',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    width: '22px',
    height: '22px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    fontFamily: 'inherit',
    lineHeight: 1,
    flexShrink: 0,
  },
  balanceChip: {
    borderRadius: '10px',
    padding: '3px 8px',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
    display: 'block',
    width: '100%',
  },
  txnTag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: '#555',
    marginTop: '3px',
    background: '#fff9e6',
    borderRadius: '4px',
    padding: '2px 4px',
  },
  txnRemoveBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    fontSize: '12px',
    padding: '0 2px',
    lineHeight: 1,
    fontFamily: 'inherit',
  },
  txnForm: {
    marginTop: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  txnInput: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '4px 6px',
    fontSize: '12px',
    width: '100%',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  txnAddBtn: {
    flex: 1,
    background: '#1b6d24',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontFamily: 'inherit',
  },
  txnCancelBtn: {
    background: '#e0e0e0',
    color: '#555',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontFamily: 'inherit',
  },
  legend: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    marginTop: '14px',
    flexWrap: 'wrap',
  },
  sidebar: {
    width: '300px',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    padding: '16px',
    flexShrink: 0,
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  sidebarTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
  },
  emptyMsg: {
    color: '#999',
    fontSize: '13px',
    textAlign: 'center',
    lineHeight: '1.6',
    margin: '8px 0',
  },
  sidebarItem: {
    borderBottom: '1px solid #f0f0f0',
    padding: '8px 0',
  },
  sidebarItemName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#333',
  },
  sidebarItemDetail: {
    fontSize: '12px',
    color: '#888',
    marginTop: '2px',
  },
  sideInput: {
    width: '100%',
    padding: '6px 8px',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
};
